import { AutoshipBadge } from 'components/badges/autoship/AutoshipBadge';
import { HelperModal } from 'components/dialogs';
import { UserHeader } from 'components/headers';
import { FormattedUnitValue, LabelledText } from 'components/text';
import { PII, usePII } from 'features/pii';
import { Icon, IconName } from 'libs/icons';
import { trackEvent } from 'libs/telemetry';
import { FixedDate } from 'libs/time';
import { useTranslation } from 'libs/translations';
import { useMemo } from 'react';
import AuthenticationService from 'services/AuthenticationService';
import { IUserProfile } from 'types';
import { AutoshipStatusType } from 'types/business-data/IAutoship';

import { AutoshipDetails } from './AutoshipDetails';

export interface IAutoshipDetails {
  readonly owner: IUserProfile;
  readonly points: number;
  readonly status: AutoshipStatusType;
  readonly type: string;
  readonly lastExecutionDate?: FixedDate;
  readonly nextExecutionDate?: FixedDate;
  readonly referenceNumber?: string;
}

const callHandler = () => {
  trackEvent({
    name: 'AutoshipCallButtonClick'
  });
  return true;
};

const sendEmailHandler = () => {
  trackEvent({
    name: 'AutoshipEmailButtonClick'
  });
  return true;
};
interface IAutoshipModalProps extends IAutoshipDetails {
  readonly onDismiss: () => void;
  readonly loadAutoshipDetails?: boolean;
}

export function AutoshipModal(props: IAutoshipModalProps) {
  const { t, activeLanguage } = useTranslation();
  const { pii } = usePII();
  const {
    status,
    lastExecutionDate,
    nextExecutionDate,
    loadAutoshipDetails,
    referenceNumber
  } = props;

  const autoshipStatusDescription = useMemo(() => {
    // If autoship is processed, we extract the day based on the last execution date, because next execution date can be modified by the user
    const executionDate =
      status === 'processed' ? lastExecutionDate : nextExecutionDate;

    switch (status) {
      case 'processed':
        return t(
          'This autoship was processed and points were credited on {{day}}. of the specified month.',
          {
            day: executionDate?.day
          }
        );
      case 'notProcessed':
        return t(
          'This autoship will be processed and points will be credited on the {{day}}. of the specified month.',
          { day: executionDate?.day }
        );
      case 'paused':
        return t(
          'This autoship is paused. The next execution is scheduled for {{date}}.',
          { date: executionDate?.toLocaleString(activeLanguage, 'long') }
        );
      case 'cancelled':
        return t(
          'This autoship was cancelled. Its last scheduled execution is on {{date}}.',
          { date: executionDate?.toLocaleString(activeLanguage, 'long') }
        );
    }
  }, [activeLanguage, lastExecutionDate, nextExecutionDate, status, t]);

  return (
    <HelperModal onDismiss={props.onDismiss}>
      <h1>{t('Autoship details')}</h1>
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {AuthenticationService.user.userId != props.owner.userId && (
          <LabelledText
            label={t('Owner')}
            value={
              <UserHeader
                user={props.owner}
                showBadges
                showCheckAssurance
                showQualification
                showNewRegistration
                disableNavigation
                showCountryFlag
              />
            }
          />
        )}

        {props.nextExecutionDate && (
          <LabelledText
            label={t('Next delivery date')}
            value={
              <PII
                value={props.nextExecutionDate.toLocaleString(
                  activeLanguage,
                  'medium'
                )}
                type="fixed-date"
              />
            }
          />
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 overflow-hidden">
        {props.owner?.phoneNumber && (
          <a
            href={`tel:${pii(
              props.owner.phoneNumber.replace(/[() -]/g, ''),
              'phone'
            )}`}
            onClick={callHandler}
            className="profile-details--call-button"
          >
            <Icon name={IconName.Call} size={16} />
            <PII value={props.owner.phoneNumber} type="phone" />
          </a>
        )}
        {props.owner?.email && (
          <a
            href={`mailto:${pii(props.owner.email, 'email')}`}
            onClick={sendEmailHandler}
            className="profile-details--email-button"
          >
            <Icon name={IconName.Email} size={16} />
            <PII value={props.owner.email} type="phone" />
          </a>
        )}
      </div>

      <LabelledText
        label={t('Points')}
        value={
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 items-center">
              <FormattedUnitValue unit="points" value={props.points} />
              <AutoshipBadge
                status={props.status}
                style="status"
                lastExecutionDate={lastExecutionDate}
                nextExecutionDate={nextExecutionDate}
              />
            </div>
            <span className="pmi-caption text-silver">
              {autoshipStatusDescription}
            </span>
          </div>
        }
      />
      {loadAutoshipDetails && referenceNumber && (
        <AutoshipDetails referenceNumber={referenceNumber} />
      )}
    </HelperModal>
  );
}
